import { useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import MoreIcon from '@mui/icons-material/MoreHoriz';
import { FONT_ACTION, FONT_PANEL_TITLE, PANEL_HEADER_HEIGHT } from '../designTokens';

export interface NotificationPanelHeaderProps {
  hasUnread: boolean;
  markingAll: boolean;
  /** No rows to act on, or a dismiss already in flight. */
  disableBulkActions: boolean;
  onMarkAllRead: () => void;
  onClearRead: () => void;
  onClearOlderThanAMonth: () => void;
  onClose: () => void;
}

/**
 * The panel's title row: "Mark all read" (SPEC "Actions") and the bulk-clear
 * menu (IAN-53, AC-50) sit beside the close control.
 *
 * The two clear options are the entire bulk-dismiss affordance — each is one
 * filtered request, never an id list, because "older than a month" or "the
 * ones I've read" can span rows this session never scrolled to.
 */
export function NotificationPanelHeader({
  hasUnread,
  markingAll,
  disableBulkActions,
  onMarkAllRead,
  onClearRead,
  onClearOlderThanAMonth,
  onClose,
}: NotificationPanelHeaderProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        height: PANEL_HEADER_HEIGHT,
        px: 1.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontSize: FONT_PANEL_TITLE, fontWeight: 600, color: 'text.primary', flex: 1 }}>
        Notifications
      </Typography>

      <Button
        size="small"
        onClick={onMarkAllRead}
        disabled={!hasUnread || markingAll}
        startIcon={
          markingAll ? <CircularProgress size={12} color="inherit" /> : <DoneAllIcon sx={{ fontSize: 14 }} />
        }
        sx={{ textTransform: 'none', fontSize: FONT_ACTION, minWidth: 0, px: 1 }}
      >
        Mark all read
      </Button>

      <Tooltip title="Clear notifications" arrow>
        <span>
          <IconButton
            size="small"
            aria-label="Clear notifications"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            disabled={disableBulkActions}
          >
            <MoreIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onClearRead();
          }}
        >
          Clear read
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onClearOlderThanAMonth();
          }}
        >
          Clear older than a month
        </MenuItem>
      </Menu>

      <IconButton size="small" aria-label="Close notifications" onClick={onClose}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

export default NotificationPanelHeader;
